<template>
  <div>
    <v-alert
      v-if="documents.length === 0 && !loading"
      class="alertStyle"
      type="info"
      text
      prominent
    >
      <v-row align="center">
        <v-col class="grow"> Denne mappen er tom </v-col>
        <v-col class="shrink">
          <v-btn color="album" @click="$router.back()">Gå tilbake</v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <div v-if="loading" class="text-center pb-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-card
      class="mb-4 justify-space-between"
      v-for="(document, index) in documents"
      :key="index"
      color="cardSurface"
      @click="$emit('click', document)"
    >
      <v-list
        class="pa-0"
        color="transparent"
        v-if="$vuetify.breakpoint.mobile"
        dense
        two-line
      >
        <v-list-item class="px-0">
          <v-checkbox
            dense
            v-model="document.selected"
            v-if="document.extension == '.pdf'"
            @click="handleSelection($event, document)"
            class="pl-4 pt-4"
          >
          </v-checkbox>
          <v-list-item-avatar class="my-0 mr-0">
            <v-icon dense :color="document.extension ? 'primary' : 'yellow'">
              {{
                document.extension ? files[document.extension] : "mdi-folder"
              }}
            </v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              <span style="font-size: 10pt">
                {{ document[itemName] }}
              </span>
            </v-list-item-title>
            <v-list-item-subtitle v-if="document.version">
              Versjon {{ document.version }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="justify-end ma-0">
            <div class="mr-2">
              <v-btn
                v-if="document.version"
                icon
                @click.stop="showVersionDialog(document)"
              >
                <v-icon color="info">mdi-information</v-icon>
              </v-btn>
              <slot :document="document"> </slot>
            </div>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>
    <!-- kun hvis ikke mobile size -->
    <v-data-table
      v-if="documents && documents.length > 0 && !$vuetify.breakpoint.mobile"
      dense
      :items-per-page="100000"
      hide-default-footer
      :items="documents"
      :headers="columns"
    >
      <template v-slot:item="{ index, item }">
        <tr @click="$emit('click', item)" style="cursor: pointer">
          <td style="max-width: 50px" v-if="displayCheckboxColumn">
            <v-checkbox
              dense
              v-model="item.selected"
              hide-details="true"
              v-if="item.extension == '.pdf'"
              @click="handleSelection($event, item)"
              class="ma-1 pa-1"
            >
            </v-checkbox>
          </td>
          <td style="max-width: 20px">
            <v-icon :color="item.extension ? 'primary' : 'yellow'">
              {{ item.extension ? files[item.extension] : "mdi-folder" }}
            </v-icon>
          </td>
          <td>
            <span>{{ item.name }}</span>
          </td>
          <td>
            <span>{{ item.version }}</span>
          </td>
          <td>
            <span v-if="item.extension">{{
              item.dateTime | moment("DD.MM.YYYY HH:mm")
            }}</span>
          </td>
          <td>
            <span class="version-comment">{{ item.versionComment }}</span>
          </td>
          <td>
            <div style="float: right">
              <v-btn
                v-if="item.version"
                icon
                @click.stop="showVersionDialog(item)"
              >
                <v-icon color="info">mdi-information</v-icon>
              </v-btn>
              <slot :document="item"></slot>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>

    <DocumentsVersionDialog
      v-model="showDialogVersion"
      :title="documentTitle"
      :document="document"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import moment from "moment";
import DocumentsVersionDialog from "@/components/shared/documents/DocumentsVersionDialog.vue";

import { DocumentObject } from "@/types/documentInterface";

export default Vue.extend({
  props: {
    documents: {
      type: Array as () => Array<DocumentObject>,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    itemName: {
      type: String,
      default: "name",
    },
  },
  data() {
    return {
      files: {
        ".html": "mdi-language-html5",
        ".js": "mdi-nodejs",
        ".json": "mdi-json",
        ".md": "mdi-markdown",
        ".pdf": "mdi-file-pdf-box",
        ".png": "mdi-file-image",
        ".jpg": "mdi-file-image",
        ".jpeg": "mdi-file-image",
        ".txt": "mdi-file-document-outline",
        ".xls": "mdi-file-excel",
        ".xlsx": "mdi-file-excel-outline",
        ".doc": "mdi-file-word",
        ".docx": "mdi-file-word-outline",
        ".pptx": "mdi-file-powerpoint-outline",
      },
      showDialogVersion: false,
      documentTitle: "",
      document: {} as DocumentObject | null,
    };
  },
  methods: {
    handleSelection(event, document) {
      event.stopPropagation();
      this.$emit("selection", document);
    },
    async showVersionDialog(document: DocumentObject) {
      this.document = document;
      this.documentTitle = this.document.name;
      this.showDialogVersion = true;
      console.info("open");
    },
  },
  computed: {
    displayCheckboxColumn(): boolean {
      var docs = this.documents as Array<any>;
      return docs && docs.filter((d) => d.extension == ".pdf").length > 0;
    },
    columns(): any {
      var headers = [
        {
          text: " ",
          value: "icon",
          type: "string",
          width: "20px",
          sortable: false,
        },
        {
          text: "Mappe / Fil",
          value: "name",
          type: "string",
          sortable: false,
        },
        {
          text: "Versjon",
          value: "version",
          type: "number",
          sortable: false,
          width: "100px",
        },
        {
          text: "Versjonsdato",
          value: "dateTime",
          type: "date",
          sortable: false,
          width: "150px",
        },
        {
          text: "Kommentar",
          value: "versionComment",
          type: "string",
          sortable: false,
        },
        {
          text: " ",
          value: "actions",
          type: "string",
          sortable: false,
        },
      ];

      if (this.displayCheckboxColumn) {
        headers.unshift({
          text: " ",
          value: "checkbox",
          type: "boolean",
          width: "50px",
          sortable: false,
        });
      }
      return headers;
    },
  },
  components: {
    DocumentsVersionDialog,
  },
});
</script>

<style lang="scss">
.alertStyle {
  .v-alert__wrapper {
    .v-alert__icon.v-icon:after {
      background: transparent !important;
    }
  }
}
.version-comment {
  display: block;
  width: 425px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
